<template>
  <div class="router-transitions container-fluid flex-grow-1 container-p-y">
    <div class="d-flex align-items-baseline w-100 py-3 mb-4">
      <h4 class="font-weight-bold mr-2">Users</h4>
      <b-input-group class="mr-3" style="min-width: 100px; max-width: 300px;">
        <b-input
          type="text"
          placeholder="Search by name or email..."
          v-model="searchTerm"
          @input="onSearchInput"
        />
        <template #append v-if="searchTerm">
          <b-btn variant="default" @click="clearSearch" title="Clear search">
            <i class="ion ion-md-close"></i>
          </b-btn>
        </template>
      </b-input-group>
      <b-btn v-if="!scimUsersSyncEnabled" variant="primary btn-round" class="d-block create-button" @click="userCreate">
        <span class="ion ion-md-add"></span>&nbsp;Create user
      </b-btn>
    </div>
    <b-card no-body>
      <div class="table-responsive">
        <b-table :items="nonLocalUsers" :fields="fields" class="card-table">
          <template #head(name)="data">
            <AppTableHeadCellSort name="name" :current="params.sort" @sort="setSort">{{ data.label }}</AppTableHeadCellSort>
          </template>
          <template #head(email)="data">
            <AppTableHeadCellSort name="email" :current="params.sort" @sort="setSort">{{ data.label }}</AppTableHeadCellSort>
          </template>
          <template #head(status)="data">
            <AppTableHeadCellSort name="status" :current="params.sort" @sort="setSort">{{ data.label }}</AppTableHeadCellSort>
          </template>
          <template #head(source)="data" v-if="hasSourceColumn">
            <AppTableHeadCellSort name="scim_data" :current="params.sort" @sort="setSort">{{ data.label }}</AppTableHeadCellSort>
          </template>
          <template #cell(role)="data">
            <span v-if="data.item.role === 'userRole'">User</span>
            <span v-if="data.item.role === 'adminRole'">Admin</span>
            <span v-if="data.item.role === 'superAdminRole'">Owner</span>
          </template>
          <template #cell(status)="data">
            <b-badge variant="outline-success" v-if="+data.item.status === 0">Deleted</b-badge>
            <b-badge variant="outline-success" v-if="+data.item.status === 10">Active</b-badge>
            <b-badge variant="outline-danger" v-if="+data.item.status === 1">Inactive</b-badge>
          </template>
          <template #cell(source)="data">
            <span v-if="data.item.source === 'scim'">SCIM</span>
          </template>
          <template #cell(actions)="data">
            <b-btn variant="default btn-sm icon-btn md-btn-flat" title="Edit" @click="edit(data.item)"
              ><i class="ion ion-md-create"></i
            ></b-btn>
          </template>
        </b-table>
      </div>
      <AppPager :pager="usersPager" @change="setPage" />
    </b-card>
    <router-view></router-view>
  </div>
</template>

<script>
import AppPager from '@/components/shared/AppPager'
import AppTableHeadCellSort from '@/components/shared/AppTableHeadCellSort.vue'
import { mapActions, mapGetters, mapState } from 'vuex'
import Id, { isLocalId } from '@/utils/Id'
import { compactQuery, replaceQuery, sameQuery } from '@/router/navigation'

export default {
  name: 'Users',
  components: {
    AppPager,
    AppTableHeadCellSort
  },
  data: () => ({
    searchTerm: '',
    params: {
      sort: 'name',
      page: '1'
    }
  }),
  computed: {
    ...mapGetters('user', ['users']),
    ...mapState('user', ['usersPager']),
    ...mapGetters('settings', ['scimUsersSyncEnabled']),
    nonLocalUsers() {
      return this.users.filter((u) => !isLocalId(u.id))
    },
    hasSourceColumn() {
      return this.scimUsersSyncEnabled
    },
    // the list as the address carries it
    query() {
      return compactQuery({ ...this.params, term: this.searchTerm })
    },
    fields() {
      return [
        { key: 'name', label: 'User Name' },
        { key: 'email' },
        { key: 'status' },
        { key: 'source', enabled: this.hasSourceColumn },
        { key: 'role' },
        { key: 'actions', label: ' ', tdClass: 'text-right mr-3 text-nowrap' }
      ].filter(({ enabled }) => enabled === undefined || enabled)
    }
  },
  watch: {
    $route(to, from) {
      if (to.name !== 'users') return
      // a link, back or forward to another search: the screen follows the address
      const moved = !sameQuery(to.query, this.query)
      if (moved) this.readQuery(to.query)
      // and back from the edit modal the list may have changed under it
      if (moved || from.name === 'user-edit') this.load()
    }
  },
  methods: {
    ...mapActions('user', ['usersLoad']),
    edit(user) {
      // the modal carries the list's address, so closing it lands on the same search
      this.$router.push({ name: 'user-edit', params: { userId: user.id }, query: this.$route.query })
    },
    userCreate() {
      this.edit({ id: Id() })
    },
    readQuery(query) {
      this.params = { sort: 'name', ...query }
      this.searchTerm = query.term || ''
    },
    load() {
      const params = { ...this.params, term: this.searchTerm || '' }
      this.usersLoad({
        params: Object.assign({ 'per-page': 50 }, params)
      })
      // only the list's own address: with the edit modal open over it (a reload of
      // /users/5 creates the list too) writing it would close the modal
      if (this.$route.name === 'users') replaceQuery(this.$router, this.query)
    },
    setSort(sort) {
      this.params.sort = sort
      this.load()
    },
    setPage(page) {
      this.params.page = page
      this.load()
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    onSearchInput() {
      this.params.page = '1'
      this.load()
    },
    clearSearch() {
      this.searchTerm = ''
      this.params.page = '1'
      this.load()
    }
  },
  created() {
    this.readQuery(this.$route.query)
    this.load()
  }
}
</script>

<style scoped>
.create-button {
  margin-left: auto;
  white-space: nowrap;
}
</style>
